import {
  SkyCard,
  SkyDropdown,
  SkyMessageBar,
  SkyTabList,
  SkyTabPanel,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import {
  DISPLAY_STATUSES,
  MONITOR_EMPTY_TABLES,
  MONITOR_HEARTBEAT_STALE_MINUTES,
  type DisplayStatus,
  type MonitorIncidentRow,
  type MonitorSummary,
} from "@shared/types/monitor";
import {
  EmptyState,
  ErrorState,
  LoadingRows,
  PermissionNotice,
} from "@/components/access/ScreenStates";
import { MonitorIncidentsGrid } from "@/components/monitor/MonitorIncidentsGrid";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";
import { MonitorSchedulesGrid } from "@/components/monitor/MonitorSchedulesGrid";
import { MonitorTableDrawer } from "@/components/monitor/MonitorTableDrawer";
import { MonitorTablesGrid } from "@/components/monitor/MonitorTablesGrid";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useMonitorChecks,
  useMonitorEnabled,
  useMonitorIncidents,
  useMonitorSummary,
} from "@/hooks/use-monitor";
import {
  hasNoFreshnessLimit,
  scheduleChecksOf,
  statusLabel,
  tablesOf,
  type WatchedTable,
} from "@/lib/monitor/format";
import { formatInstant } from "@/lib/time/displayTime";
import { FeatureOffNotice } from "@/components/shell/FeatureOffNotice";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { ROUTES, absolutePath } from "@/lib/routes";
import { Link, useSearch } from "wouter";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { byDisplayText } from "@shared/sort";

/**
 * skyMonitor (docs/research/2026-09-28-skymonitor-spec.md, section 11): the page that
 * says, before anyone's users notice, which tables are late, empty or unusual and which schedules
 * did not run. Read-only in phase 1: the controls (edit a check, mute, "Assign to me", "Not an
 * issue") are phase 2, and their audited routes already exist.
 *
 * Behind `features.flags.monitor`: off, the nav entry is gone and this page says skyMonitor is
 * off. Everything here comes from skyMonitor's own records of skyData's loads and runs.
 */

type Tab = "tables" | "schedules" | "incidents";

/** A tab by its value; anything else is the first tab. */
function tabOf(value: string | null | undefined): Tab {
  return value === "schedules" || value === "incidents" ? value : "tables";
}

function SummaryCard({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <SkyCard appearance="outline" testId={testId}>
      <div className="flex flex-col gap-1">
        <SkyText size={200} color="muted">
          {label}
        </SkyText>
        {/* Round 7 T-37: a key number is 20 px, as on Overview (it was 24 here). */}
        <SkyText size={500} weight="semibold" font="numeric">
          {value.toLocaleString()}
        </SkyText>
      </div>
    </SkyCard>
  );
}

function LastReported({ summary }: { summary: MonitorSummary }) {
  const zone = useDisplayTz();
  if (summary.monitorStale) {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        title="skyMonitor is not reporting"
        message={
          summary.monitorLastSeenAt
            ? `It last reported at ${formatInstant(summary.monitorLastSeenAt, zone, "short", { named: true })}, more than ${MONITOR_HEARTBEAT_STALE_MINUTES} minutes ago. What this page shows may be out of date.`
            : "It has not reported yet. Once it runs, it checks every table and schedule within a few minutes."
        }
        testId="monitor-not-reporting"
      />
    );
  }
  return (
    <SkyText size={200} color="muted" block testId="monitor-last-reported">
      skyMonitor last reported at{" "}
      {formatInstant(summary.monitorLastSeenAt, zone, "short", { named: true })}
      {summary.lastEvaluatedAt
        ? `. Last check at ${formatInstant(summary.lastEvaluatedAt, zone, "short", { named: true })}`
        : ""}
      .
    </SkyText>
  );
}

/**
 * Where this page's alerts are set (owner, round 3, 2026-09-30: "Should we add notification on
 * this page for alerting or is it taken care in schedules?"): they are the Data health rules of
 * Settings > Notifications, so the page says so, with a link that opens that section for someone
 * who may change it, and says who does for everyone else.
 */
function AlertsLine({ canManage }: { canManage: boolean }) {
  return (
    <SkyText size={200} color="muted" block testId="monitor-alerts-line">
      {canManage ? (
        <>
          {"Alerts for these checks are set in "}
          <Link
            // `add=1` (B-62): with no rule yet the section is not on the page, so the link also asks
            // for the pre-filled new rule, whose Data health section is open. Ignored once rules exist.
            href={absolutePath(`${ROUTES.settingsNotifications}?open=data_health&add=1`)}
            className="font-medium underline underline-offset-2"
            data-testid="monitor-alerts-link"
          >
            Settings &gt; Notifications &gt; Data health
          </Link>
          .
        </>
      ) : (
        "Your admin sets alerts for these checks."
      )}
    </SkyText>
  );
}

/** One incidents read: its error with Retry, its loading rows, or the grid. */
function IncidentsList({
  incidents,
  things,
  errorTitle,
  loadingRows,
  closed = false,
}: {
  incidents: UseQueryResult<MonitorIncidentRow[]>;
  /** The bar's words for this list when a re-read fails ("open incidents"). */
  things: string;
  errorTitle: string;
  loadingRows: number;
  closed?: boolean;
}) {
  // #1398: only a failed first read replaces the list; a failed re-read keeps the rows.
  if (incidents.isError && !incidents.data) {
    return (
      <ErrorState
        title={errorTitle}
        error={incidents.error}
        onRetry={() => void incidents.refetch()}
      />
    );
  }
  if (incidents.isPending) return <LoadingRows rows={loadingRows} columns={6} />;
  return (
    <>
      {incidents.isError ? (
        <RefreshErrorBar
          things={things}
          error={incidents.error}
          onRetry={() => void incidents.refetch()}
          empty={(incidents.data ?? []).length === 0}
          testId={
            closed ? "monitor-closed-incidents-refresh-error" : "monitor-incidents-refresh-error"
          }
        />
      ) : null}
      <MonitorIncidentsGrid rows={incidents.data ?? []} closed={closed} />
    </>
  );
}

export default function MonitorPage() {
  const { canAny, isResolved } = usePermissions();
  const canRead = canAny("monitor.read");
  const canManageAlerts = canAny("instance.settings.write");
  const enabled = useMonitorEnabled();
  const active = canRead && enabled;
  const summary = useMonitorSummary(active);
  const checks = useMonitorChecks(active);
  const incidents = useMonitorIncidents(active);
  // #842: `?tab=incidents` (a failed run's "Open the alert" link) opens that tab first.
  const search = useSearch();
  const [tab, setTab] = useState<Tab>(() => tabOf(new URLSearchParams(search).get("tab")));
  // Round 7: the closed incidents (why each closed) are read once the Incidents tab is open.
  const closedIncidents = useMonitorIncidents(active && tab === "incidents", "closed");
  const [connection, setConnection] = useState<string>("");
  const [status, setStatus] = useState<DisplayStatus | "">("");
  const [opened, setOpened] = useState<WatchedTable | null>(null);

  if (!isResolved) return <LoadingRows rows={4} columns={5} />;
  if (!canRead) return <PermissionNotice permission="monitor.read" />;
  if (!enabled) return <FeatureOffNotice feature="monitor" />;
  if (summary.isPending || checks.isPending) return <LoadingRows rows={4} columns={6} />;
  // #1398: a failed re-read of the checks with checks on screen keeps the page (the bar below).
  if (summary.isError || !summary.data || !checks.data) {
    return (
      <ErrorState
        title="Data health could not be loaded"
        error={summary.error ?? checks.error}
        onRetry={() => {
          void summary.refetch();
          void checks.refetch();
        }}
      />
    );
  }

  const tables = tablesOf(checks.data);
  const schedules = scheduleChecksOf(checks.data);
  const connections = [
    ...new Set(tables.map((row) => row.connectionName ?? "Deleted connection")),
  ].sort(byDisplayText);
  const shown = tables.filter(
    (row) =>
      (!connection || (row.connectionName ?? "Deleted connection") === connection) &&
      (!status || row.status === status),
  );
  const s = summary.data;
  // Round 6 W15: the connections whose tables read Unknown because no schedule imports them.
  const unscheduled = [
    ...new Set(
      tables
        .filter((row) => row.status === "unknown" && hasNoFreshnessLimit(row))
        .map((row) => row.connectionName ?? "Deleted connection"),
    ),
  ].sort(byDisplayText);

  return (
    <div className="flex flex-col gap-4" data-testid="monitor-page">
      <div className="flex flex-col gap-1">
        <AlertsLine canManage={canManageAlerts} />
        <LastReported summary={s} />
        <ZoneCaption testId="monitor-zone-caption" />
      </div>
      <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-4 md:grid-cols-4">
        <SummaryCard
          label="Tables watched"
          value={s.tables.watched}
          testId="monitor-summary-watched"
        />
        {/* Round 9 (B-31): these are table counts; schedules and incidents are on their tabs. */}
        <SummaryCard
          label="Tables failing"
          value={s.tables.failing}
          testId="monitor-summary-failing"
        />
        <SummaryCard
          label="Tables warning"
          value={s.tables.warning}
          testId="monitor-summary-warning"
        />
        <SummaryCard
          label="Tables training"
          value={s.tables.training}
          testId="monitor-summary-training"
        />
      </div>
      <SkyText size={200} color="muted" block testId="monitor-summary-schedules">
        {`Schedules: ${s.schedules.watched.toLocaleString()} watched, ${s.schedules.failing.toLocaleString()} failing, ${s.schedules.warning.toLocaleString()} warning. Open incidents: ${s.openIncidents.toLocaleString()}.`}
      </SkyText>
      <SkyTabList
        tabs={[
          { value: "tables", label: "Tables" },
          { value: "schedules", label: "Schedules" },
          { value: "incidents", label: `Incidents (${s.openIncidents.toLocaleString()})` },
        ]}
        selectedValue={tab}
        onTabSelect={(value) => setTab(tabOf(value))}
        ariaLabel="Data health"
      >
        <SkyTabPanel value="tables">
          {/* #1398: the checks feed the Tables and Schedules tabs, so each says when a re-read failed. */}
          {checks.isError ? (
            <RefreshErrorBar
              things="tables"
              error={checks.error}
              onRetry={() => void checks.refetch()}
              empty={shown.length === 0}
              testId="monitor-tables-refresh-error"
            />
          ) : null}
          {tables.length === 0 ? (
            <EmptyState title="Nothing to watch yet" message={MONITOR_EMPTY_TABLES} />
          ) : (
            <div className="flex flex-col gap-4 pt-2">
              <div className="flex flex-wrap gap-4">
                <div className="w-64">
                  <SkyDropdown
                    label="Connection"
                    testId="monitor-filter-connection"
                    selectedOptions={[connection]}
                    options={[
                      { value: "", text: "Every connection" },
                      ...connections.map((name) => ({ value: name, text: name })),
                    ]}
                    onOptionSelect={(values) => setConnection(values[0] ?? "")}
                  />
                </div>
                <div className="w-48">
                  <SkyDropdown
                    label="Status"
                    testId="monitor-filter-status"
                    selectedOptions={[status]}
                    options={[
                      { value: "", text: "Every status" },
                      ...DISPLAY_STATUSES.map((value) => ({ value, text: statusLabel(value) })),
                    ]}
                    onOptionSelect={(values) => setStatus((values[0] ?? "") as DisplayStatus | "")}
                  />
                </div>
              </div>
              {unscheduled.length > 0 ? (
                <div className="flex flex-col gap-1" data-testid="monitor-unknown-hint">
                  {unscheduled.map((name) => (
                    <SkyText key={name} size={200} color="muted" block>
                      {`No freshness limit for ${name}: no schedule covers this connection, so its tables read Unknown.`}
                    </SkyText>
                  ))}
                </div>
              ) : null}
              <MonitorTablesGrid rows={shown} onOpen={setOpened} />
            </div>
          )}
        </SkyTabPanel>
        <SkyTabPanel value="schedules">
          {checks.isError ? (
            <RefreshErrorBar
              things="schedules"
              error={checks.error}
              onRetry={() => void checks.refetch()}
              empty={schedules.length === 0}
              testId="monitor-schedules-refresh-error"
            />
          ) : null}
          <div className="pt-2">
            <MonitorSchedulesGrid rows={schedules} />
          </div>
        </SkyTabPanel>
        <SkyTabPanel value="incidents">
          <div className="pt-2">
            <IncidentsList
              incidents={incidents}
              things="open incidents"
              errorTitle="The incidents could not be loaded"
              loadingRows={3}
            />
          </div>
          {/* Round 7 (owner 2026-10-03): the last 30 days' closed incidents, each saying why. */}
          <div className="flex flex-col gap-2 pt-6">
            <SkyText as="h2" size={400} weight="semibold">
              Closed in the last 30 days
            </SkyText>
            <IncidentsList
              incidents={closedIncidents}
              things="closed incidents"
              errorTitle="The closed incidents could not be loaded"
              loadingRows={2}
              closed
            />
          </div>
        </SkyTabPanel>
      </SkyTabList>
      <MonitorTableDrawer table={opened} onClose={() => setOpened(null)} />
    </div>
  );
}
